import { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Pencil, Check, X, Copy, Trash2, Upload, Image, Clipboard, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface LoadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (planId: string) => void;
  onNameUpdate?: (planId: string, newName: string) => void;
}

const LoadDialog = ({ open, onOpenChange, onSelect, onNameUpdate }: LoadDialogProps) => {
  const { user } = useAuth();
  const [plans, setPlans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [uploadingImageId, setUploadingImageId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const loadPlans = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        console.log('Loading plans for user:', user.id);
        const { data, error } = await supabase
          .from('floor_plans')
          .select('id, name, updated_at, preview_image')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        
        console.log('Loaded plans:', data);
        setPlans(data || []);
      } catch (error: any) {
        console.error('Error loading plans:', error);
        toast({
          title: "Fout bij laden",
          description: "Kon plattegronden niet laden",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      loadPlans();
    }
  }, [open, user, toast]);

  const handleSelect = (planId: string) => {
    console.log('Selecting plan:', planId);
    onSelect(planId);
    onOpenChange(false);
  };

  const handleStartEdit = (plan: any) => {
    setEditingId(plan.id);
    setEditingName(plan.name);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingName.trim()) return;

    try {
      const { error } = await supabase
        .from('floor_plans')
        .update({ name: editingName.trim() })
        .eq('id', editingId)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Update local state
      setPlans(plans.map(plan => 
        plan.id === editingId ? { ...plan, name: editingName.trim() } : plan
      ));

      // Notify parent component about the name change
      if (onNameUpdate) {
        onNameUpdate(editingId, editingName.trim());
      }

      toast({
        title: "Naam bijgewerkt",
        description: "De plattegrondnaam is succesvol gewijzigd",
      });

      setEditingId(null);
      setEditingName('');
    } catch (error: any) {
      console.error('Error updating plan name:', error);
      toast({
        title: "Fout bij opslaan",
        description: "Kon de naam niet bijwerken",
        variant: "destructive",
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleDuplicate = async (plan: any) => {
    try {
      // First get the full plan data
      const { data: fullPlan, error: fetchError } = await supabase
        .from('floor_plans')
        .select('*')
        .eq('id', plan.id)
        .eq('user_id', user?.id)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('floor_plans')
        .insert({
          name: `${plan.name} (kopie)`,
          content: fullPlan.content,
          user_id: user?.id,
          preview_image: fullPlan.preview_image,
          is_public: fullPlan.is_public,
          is_active: false,
          is_favorite: false
        });

      if (error) throw error;

      // Reload plans to show the new duplicate
      const { data, error: reloadError } = await supabase
        .from('floor_plans')
        .select('id, name, updated_at, preview_image')
        .eq('user_id', user?.id)
        .order('updated_at', { ascending: false });

      if (reloadError) throw reloadError;
      
      setPlans(data || []);

      toast({
        title: "Plattegrond gekopieerd",
        description: "De plattegrond is succesvol gekopieerd",
      });
    } catch (error: any) {
      console.error('Error duplicating plan:', error);
      toast({
        title: "Fout bij kopiëren",
        description: "Kon de plattegrond niet kopiëren",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (plan: any) => {
    try {
      const { error } = await supabase
        .from('floor_plans')
        .delete()
        .eq('id', plan.id)
        .eq('user_id', user?.id);

      if (error) throw error;

      setPlans(plans.filter(p => p.id !== plan.id));

      toast({
        title: "Plattegrond verwijderd",
        description: "De plattegrond is succesvol verwijderd",
      });
    } catch (error: any) {
      console.error('Error deleting plan:', error);
      toast({
        title: "Fout bij verwijderen",
        description: "Kon de plattegrond niet verwijderen",
        variant: "destructive",
      });
    }
  };

  const processAndSaveImage = async (planId: string, imageData: string) => {
    try {
      setUploadingImageId(planId);

      const { error } = await supabase
        .from('floor_plans')
        .update({ preview_image: imageData })
        .eq('id', planId)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Update local state
      setPlans(plans.map(plan => 
        plan.id === planId ? { ...plan, preview_image: imageData } : plan
      ));

      toast({
        title: "Afbeelding opgeslagen",
        description: "De afbeelding is succesvol toegevoegd",
      });
    } catch (error: any) {
      console.error('Error saving image:', error);
      toast({
        title: "Fout bij opslaan",
        description: "Kon de afbeelding niet opslaan",
        variant: "destructive",
      });
    } finally {
      setUploadingImageId(null);
    }
  };

  const handleImageUpload = async (planId: string, file: File) => {
    if (!file || !user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Ongeldig bestand",
        description: "Alleen afbeeldingen zijn toegestaan",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Bestand te groot",
        description: "Afbeelding mag maximaal 2MB zijn",
        variant: "destructive",
      });
      return;
    }

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Image = e.target?.result as string;
        await processAndSaveImage(planId, base64Image);
      };

      reader.readAsDataURL(file);
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: "Fout bij uploaden",
        description: "Kon de afbeelding niet uploaden",
        variant: "destructive",
      });
      setUploadingImageId(null);
    }
  };

  const handlePasteImage = async (planId: string) => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/')) {
            const blob = await clipboardItem.getType(type);
            
            // Check size
            if (blob.size > 2 * 1024 * 1024) {
              toast({
                title: "Afbeelding te groot",
                description: "Afbeelding mag maximaal 2MB zijn",
                variant: "destructive",
              });
              return;
            }

            const reader = new FileReader();
            reader.onload = async (e) => {
              const base64Image = e.target?.result as string;
              await processAndSaveImage(planId, base64Image);
            };
            reader.readAsDataURL(blob);
            return;
          }
        }
      }
      
      toast({
        title: "Geen afbeelding gevonden",
        description: "Er is geen afbeelding in het klembord",
        variant: "destructive",
      });
    } catch (error: any) {
      console.error('Error pasting image:', error);
      toast({
        title: "Fout bij plakken",
        description: "Kon de afbeelding niet plakken. Zorg ervoor dat je een afbeelding hebt gekopieerd.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveImage = async (planId: string) => {
    try {
      const { error } = await supabase
        .from('floor_plans')
        .update({ preview_image: null })
        .eq('id', planId)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Update local state
      setPlans(plans.map(plan => 
        plan.id === planId ? { ...plan, preview_image: null } : plan
      ));

      toast({
        title: "Afbeelding verwijderd",
        description: "De afbeelding is succesvol verwijderd",
      });
    } catch (error: any) {
      console.error('Error removing image:', error);
      toast({
        title: "Fout bij verwijderen",
        description: "Kon de afbeelding niet verwijderen",
        variant: "destructive",
      });
    }
  };

  // Filter plans based on search query
  const filteredPlans = plans.filter(plan => 
    plan.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Plattegrond openen</DialogTitle>
          <DialogDescription>
            Kies een eerder opgeslagen plattegrond
          </DialogDescription>
        </DialogHeader>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek plattegrond..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <ScrollArea className="h-[500px] w-full rounded-md border p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPlans.map((plan) => (
                <div key={plan.id} className="border rounded-lg p-3">
                  {/* Preview Image - Rechthoekig formaat voor screenshots */}
                  <div className="mb-3">
                    {plan.preview_image ? (
                      <div className="relative">
                        <img
                          src={plan.preview_image}
                          alt={`Voorvertoning van ${plan.name}`}
                          className="w-full h-32 object-cover rounded border"
                          style={{ aspectRatio: '16/9' }}
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0"
                          onClick={() => handleRemoveImage(plan.id)}
                          title="Afbeelding verwijderen"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div 
                        className="w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50"
                        style={{ aspectRatio: '16/9' }}
                      >
                        <div className="text-center">
                          <Image className="h-6 w-6 mx-auto mb-1 text-gray-400" />
                          <span className="text-xs text-gray-500">Geen afbeelding</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Plan Name and Actions */}
                  <div className="flex items-center gap-2">
                    {editingId === plan.id ? (
                      <>
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveEdit();
                            } else if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                          autoFocus
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={handleSaveEdit}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={handleCancelEdit}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          className="flex-1 justify-start text-left font-normal"
                          onClick={() => handleSelect(plan.id)}
                        >
                          <span className="truncate">{plan.name}</span>
                          <span className="ml-auto text-xs text-muted-foreground">
                            {new Date(plan.updated_at).toLocaleDateString()}
                          </span>
                        </Button>
                        
                        {/* Paste Image Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handlePasteImage(plan.id)}
                          title="Afbeelding plakken uit klembord"
                          disabled={uploadingImageId === plan.id}
                        >
                          {uploadingImageId === plan.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Clipboard className="h-4 w-4" />
                          )}
                        </Button>

                        {/* Upload Image Button */}
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleImageUpload(plan.id, file);
                              }
                              e.target.value = '';
                            }}
                            className="hidden"
                            id={`image-upload-${plan.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => document.getElementById(`image-upload-${plan.id}`)?.click()}
                            title="Afbeelding uploaden"
                            disabled={uploadingImageId === plan.id}
                          >
                            {uploadingImageId === plan.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4" />
                            )}
                          </Button>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleStartEdit(plan)}
                          title="Naam bewerken"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDuplicate(plan)}
                          title="Dupliceren"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700"
                              title="Verwijderen"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Plattegrond verwijderen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je "{plan.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(plan)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Verwijderen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {plans.length === 0 && (
                <div className="text-center text-muted-foreground py-4">
                  Geen plattegronden gevonden
                </div>
              )}
              {plans.length > 0 && filteredPlans.length === 0 && (
                <div className="text-center text-muted-foreground py-4">
                  Geen plattegronden gevonden met "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default LoadDialog;
